import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, PlusIcon, MicIcon, ArrowUpIcon, MapPinIcon, CheckIcon, Loader2Icon, RotateCcwIcon, WandSparklesIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { interpretBrief, buildQuestions, applyAnswer, buildNotice, CHANNEL_NAMES, type AtnaUnderstanding, type AtnaQuestion, type AtnaDraft } from '../../utils/atna';
import type { ChannelsState } from '../../types/composer';

/** One turn in the ATNA conversation. */
type Turn = {
  kind: 'user';
  id: string;
  text: string;
} | {
  kind: 'understood';
  id: string;
  understanding: AtnaUnderstanding;
} | {
  kind: 'question';
  id: string;
  question: AtnaQuestion;
} | {
  kind: 'ready';
  id: string;
  understanding: AtnaUnderstanding;
};
interface AtnaBriefProps {
  /** Current channel content, used as the base ATNA writes onto. */
  channels: ChannelsState;
  contentLang: 'fr' | 'en';
  onGenerate: (draft: AtnaDraft) => void;
}
const SUGGESTIONS: {
  fr: string;
  en: string;
}[] = [{
  fr: 'Avis d’ébullition d’eau',
  en: 'Boil water advisory'
}, {
  fr: 'Fermeture de rue pour travaux',
  en: 'Street closure for roadwork'
}, {
  fr: 'Collecte reportée',
  en: 'Collection postponed'
}, {
  fr: 'Inviter à un événement',
  en: 'Invite to an event'
}];
export function AtnaBrief({
  channels,
  contentLang,
  onGenerate
}: AtnaBriefProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [input, setInput] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [understanding, setUnderstanding] = useState<AtnaUnderstanding | null>(null);
  const [queue, setQueue] = useState<AtnaQuestion[]>([]);
  const [thinking, setThinking] = useState(false);
  const [generated, setGenerated] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const started = turns.length > 0;
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth'
    });
  }, [turns, thinking]);
  const nextId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

  /** Queues ATNA's reply after a short pause so the exchange reads naturally. */
  const reply = (build: () => Turn[]) => {
    setThinking(true);
    window.setTimeout(() => {
      setThinking(false);
      setTurns(prev => [...prev, ...build()]);
    }, 700);
  };
  const submitBrief = (text: string) => {
    const brief = text.trim();
    if (!brief) return;
    setInput('');
    setGenerated(false);
    const u = interpretBrief(brief);
    setUnderstanding(u);
    setQueue(buildQuestions(u));
    setTurns([{
      kind: 'user',
      id: nextId(),
      text: brief
    }]);
    reply(() => [{
      kind: 'understood',
      id: nextId(),
      understanding: u
    }]);
  };

  /** Moves to the next gap, or offers to write the notice when none are left. */
  const advance = (u: AtnaUnderstanding, remaining: AtnaQuestion[]) => {
    setQueue(remaining);
    reply(() => remaining.length > 0 ? [{
      kind: 'question',
      id: nextId(),
      question: remaining[0]
    }] : [{
      kind: 'ready',
      id: nextId(),
      understanding: u
    }]);
  };
  const confirmSubject = () => {
    if (!understanding) return;
    setTurns(prev => [...prev, {
      kind: 'user',
      id: nextId(),
      text: t('Oui, c’est bien ça', 'Yes, that’s right')
    }]);
    advance(understanding, queue);
  };
  const answerQuestion = (question: AtnaQuestion, value: string, label: string) => {
    if (!understanding) return;
    const updated = applyAnswer(understanding, question.id, value, contentLang);
    setUnderstanding(updated);
    setTurns(prev => [...prev, {
      kind: 'user',
      id: nextId(),
      text: label
    }]);
    advance(updated, queue.slice(1));
  };

  /** Free-text reply: answers the open question, or refines the whole brief. */
  const submitReply = () => {
    const text = input.trim();
    if (!text) return;
    const open = queue[0];
    const lastTurn = turns[turns.length - 1];
    if (open && lastTurn?.kind === 'question') {
      answerQuestion(open, text, text);
      setInput('');
      return;
    }
    submitBrief(text);
  };
  const generate = () => {
    if (!understanding) return;
    onGenerate(buildNotice(understanding, channels, contentLang));
    setGenerated(true);
  };
  const restart = () => {
    setTurns([]);
    setUnderstanding(null);
    setQueue([]);
    setGenerated(false);
    setInput('');
  };
  const avatar = <span className="w-8 h-8 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-3646" data-mp="2cpsrkb7ht18x">
      <SparklesIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-3647" data-mp="1xtbgll37tomw" />
    </span>;
  const card = (children: React.ReactNode) => <div className="flex items-start gap-2.5" data-id="element-3648" data-mp="22seac9dqw8fk">
      {avatar}
      <div className="flex-1 min-w-0 rounded-xl border border-slate-200 bg-white px-4 py-3.5" data-id="element-3649" data-mp="3s6sxwqd11p9h">
        {children}
      </div>
    </div>;
  return <section className="space-y-6" data-id="element-3650" data-mp="zq7i150jn0b">
      <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2" data-id="element-3651" data-mp="1liagnhbjzk7p">
        {t('Créer avec ATNA', 'Create with ATNA')}
      </h2>

      <div className="bg-white rounded-xl border-[0.5px] border-slate-200 overflow-hidden" data-id="element-3652" data-mp="1gkim0fjixjzt">
        {/* Conversation */}
        <AnimatePresence initial={false} data-id="element-3653" data-mp="1v9eu2clkyx0f">
          {started && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.22
        }} className="overflow-hidden border-b border-slate-100" data-id="element-3654" data-mp="1qgc74nbb6lt7">
              <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-100" data-id="element-3655" data-mp="2e5n2tqdsxq0h">
                <div className="flex items-center gap-2.5 min-w-0" data-id="element-3656" data-mp="298eipk93fbwl">
                  {avatar}
                  <div className="min-w-0" data-id="element-3657" data-mp="jtgqgo96936l">
                    <p className="text-sm font-bold text-[#0F172A]" data-id="element-3658" data-mp="etu33lzmq6k2">ATNA</p>
                    <p className="text-[11px] text-slate-500 truncate" data-id="element-3659" data-mp="evyfytfiafwh">
                      {t("Création d'un avis", 'Creating a notice')}
                    </p>
                  </div>
                </div>
                <button type="button" onClick={restart} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-md transition-colors" data-id="element-3660" data-mp="2jx148bpjydgn">
                  <RotateCcwIcon className="w-3.5 h-3.5" data-id="element-3661" data-mp="2xsv2m0sp9kfi" />
                  {t('Recommencer', 'Start over')}
                </button>
              </div>

              <div ref={scrollRef} className="max-h-[26rem] overflow-y-auto px-5 py-4 space-y-3.5 bg-slate-50/60" data-id="element-3662" data-mp="37ltsdlzxnh0p">
                {turns.map(turn => {
              if (turn.kind === 'user') {
                return <div key={turn.id} className="flex justify-end" data-id="element-3663" data-mp="3lk0jmjadmire">
                        <p className="max-w-[80%] rounded-xl bg-[#0F172A] text-white text-sm font-medium px-3.5 py-2" data-id="element-3664" data-mp="3li7cz3zrjyje">
                          {turn.text}
                        </p>
                      </div>;
              }
              if (turn.kind === 'understood') {
                const u = turn.understanding;
                return <div key={turn.id} data-id="element-3665" data-mp="3hqsdg1i1ymok">
                        {card(<>
                            <p className="flex items-center gap-1.5 text-xs font-bold text-[#4F46E5] mb-2" data-id="element-3666" data-mp="ibewwm59a07m">
                              <SparklesIcon className="w-3.5 h-3.5" data-id="element-3667" data-mp="8jrbp2zalfel" />
                              {t('J’ai compris :', 'Here’s what I understood:')}
                            </p>
                            <h3 className="text-lg font-bold text-[#0F172A] leading-snug" data-id="element-3668" data-mp="4g31ecja05pn">
                              {u.title}
                            </h3>
                            <p className="text-sm text-slate-500 mt-0.5" data-id="element-3669" data-mp="w8jya8u0yawj">
                              {u.categoryLabel[lang]}
                              {u.urgent && <span className="ml-2 px-1.5 py-0.5 rounded bg-red-50 text-red-600 text-[10px] font-bold uppercase" data-id="element-3670" data-mp="1kom5lmazg94q">
                                  {t('urgent', 'urgent')}
                                </span>}
                            </p>
                            <p className="text-sm text-slate-600 mt-3" data-id="element-3671" data-mp="1ync0qwe8ryqg">
                              {t('Est-ce bien le sujet de votre avis ?', 'Is this the subject of your notice?')}
                            </p>
                            {turns[turns.length - 1]?.id === turn.id && !thinking && <div className="flex flex-wrap gap-2 mt-3" data-id="element-3672" data-mp="da0u758qujzc">
                                <button type="button" onClick={confirmSubject} className="px-3.5 py-2 rounded-lg bg-[#0F172A] text-white text-xs font-semibold hover:bg-[#1E293B] transition-colors" data-id="element-3673" data-mp="i9lv3l7h52ep">
                                  {t('Oui, c’est bien ça', 'Yes, that’s right')}
                                </button>
                                <button type="button" onClick={restart} className="px-3.5 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-50 transition-colors" data-id="element-3674" data-mp="31hsmnrxykyyp">
                                  {t('Non, reformuler', 'No, rephrase')}
                                </button>
                              </div>}
                          </>)}
                      </div>;
              }
              if (turn.kind === 'question') {
                const q = turn.question;
                const isOpen = turns[turns.length - 1]?.id === turn.id && !thinking;
                return <div key={turn.id} data-id="element-3675" data-mp="30qfff3kz54zs">
                        {card(<>
                            <p className="flex items-center gap-1.5 text-xs font-bold text-[#4F46E5] mb-2" data-id="element-3676" data-mp="2vrccppsgvauz">
                              {q.id === 'where' ? <MapPinIcon className="w-3.5 h-3.5" data-id="element-3677" data-mp="2m2ue9f7ntfui" /> : <SparklesIcon className="w-3.5 h-3.5" data-id="element-3678" data-mp="2h2zmzajz2o8x" />}
                              {q.prompt[lang]}
                            </p>
                            {q.guess && <>
                                <p className="text-base font-bold text-[#0F172A] leading-snug" data-id="element-3679" data-mp="284w0oc15xng2">
                                  {q.guess}
                                </p>
                                {q.guessHint && <p className="text-xs text-slate-400 mt-0.5" data-id="element-3680" data-mp="nkiij9wjetxi">
                                    {q.guessHint[lang]}
                                  </p>}
                              </>}
                            {isOpen && <div className="flex flex-wrap gap-2 mt-3" data-id="element-3681" data-mp="3w13vf1sai75w">
                                {q.options.map((opt, index) => <button key={opt.value} type="button" onClick={() => answerQuestion(q, opt.value, opt.label[lang])} className={`px-3.5 py-2 rounded-lg text-xs font-semibold transition-colors ${index === 0 ? 'bg-[#0F172A] text-white hover:bg-[#1E293B]' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`} data-id="element-3682" data-mp="4usnclov4652" data-mpi={index}>
                                    {opt.label[lang]}
                                  </button>)}
                              </div>}
                            {isOpen && q.freeText && <p className="text-[11px] text-slate-400 mt-2.5" data-id="element-3683" data-mp="l36fhyjzojp">
                                {q.freeText[lang]}
                              </p>}
                          </>)}
                      </div>;
              }
              const u = turn.understanding;
              return <div key={turn.id} data-id="element-3684" data-mp="1lw1v0vyamsxr">
                      {card(<>
                          <p className="flex items-center gap-1.5 text-xs font-bold text-[#4F46E5] mb-2" data-id="element-3685" data-mp="1tejadisup1yn">
                            <CheckIcon className="w-3.5 h-3.5" data-id="element-3686" data-mp="1ygicyqgrnuuj" />
                            {t('Tout est prêt', 'Everything is ready')}
                          </p>
                          <dl className="space-y-1.5 text-sm" data-id="element-3687" data-mp="1oi2l2l9x0hjz">
                            <div className="flex gap-2" data-id="element-3688" data-mp="2c6ls6xh8aoah">
                              <dt className="text-slate-400 w-20 flex-shrink-0" data-id="element-3689" data-mp="2c73x0ihyiau0">
                                {t('Sujet', 'Subject')}
                              </dt>
                              <dd className="text-slate-800 font-medium min-w-0" data-id="element-3690" data-mp="22hkdrbqc0eym">
                                {u.title}
                              </dd>
                            </div>
                            <div className="flex gap-2" data-id="element-3691" data-mp="2v57v8hwb2ph1">
                              <dt className="text-slate-400 w-20 flex-shrink-0" data-id="element-3692" data-mp="2v3dc994lh7fl">
                                {t('Catégorie', 'Category')}
                              </dt>
                              <dd className="text-slate-800" data-id="element-3693" data-mp="1c9fwh8tyfi4u">{u.categoryLabel[lang]}</dd>
                            </div>
                            {u.when && <div className="flex gap-2" data-id="element-3694" data-mp="128vshyf1vzix">
                                <dt className="text-slate-400 w-20 flex-shrink-0" data-id="element-3695" data-mp="12bi90njx5pda">
                                  {t('Quand', 'When')}
                                </dt>
                                <dd className="text-slate-800" data-id="element-3696" data-mp="1uxkvy43kwgxu">{u.when}</dd>
                              </div>}
                            <div className="flex gap-2" data-id="element-3697" data-mp="1l89qcrfwhuep">
                              <dt className="text-slate-400 w-20 flex-shrink-0" data-id="element-3698" data-mp="1l6q8wxiiddbc">
                                {t('Canaux', 'Channels')}
                              </dt>
                              <dd className="text-slate-800" data-id="element-3699" data-mp="2dpvslg7n3ini">
                                {u.channels.map(c => CHANNEL_NAMES[c][lang]).join(', ')}
                              </dd>
                            </div>
                          </dl>

                          {generated ? <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 rounded-md" data-id="element-3700" data-mp="2l9ej07j7qe64">
                              <CheckIcon className="w-3.5 h-3.5" data-id="element-3701" data-mp="2v72hn8jxw5tk" />
                              {t('Avis généré — vérifiez-le ci-dessous', 'Notice generated — review it below')}
                            </p> : <button type="button" onClick={generate} className="mt-3 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#4F46E5] text-white text-xs font-semibold hover:bg-[#4338CA] transition-colors" data-id="element-3702" data-mp="38yh820g6f9an">
                              <WandSparklesIcon className="w-3.5 h-3.5" data-id="element-3703" data-mp="2y6t3ht2bf5iw" />
                              {t('Générer l’avis complet', 'Generate the full notice')}
                            </button>}
                        </>)}
                    </div>;
            })}

                {thinking && <div className="flex items-center gap-2.5" data-id="element-3704" data-mp="3342sj7lslf67">
                    {avatar}
                    <span className="inline-flex items-center gap-2 text-xs text-slate-400" data-id="element-3705" data-mp="3gznb21xlzvd3">
                      <Loader2Icon className="w-3.5 h-3.5 animate-spin" data-id="element-3706" data-mp="3by7qeazcfqv8" />
                      {t('ATNA réfléchit…', 'ATNA is thinking…')}
                    </span>
                  </div>}
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Composer */}
        <div className="p-5" data-id="element-3707" data-mp="1wjthh2mzk1z6">
          <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 shadow-sm focus-within:border-[#4F46E5] transition-colors" data-id="element-3708" data-mp="1riw1mwpg7k0u">
            <PlusIcon className="w-5 h-5 text-slate-400 flex-shrink-0" data-id="element-3709" data-mp="72odk2jyqdaq" />
            <input type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submitReply();
            }
          }} aria-label={t('Demander à ATNA', 'Ask ATNA')} placeholder={started ? t('Répondre à ATNA, ou préciser votre demande…', 'Reply to ATNA, or refine your request…') : t('Décrivez votre avis à ATNA…', 'Describe your notice to ATNA…')} className="flex-1 min-w-0 bg-transparent text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none py-1.5" data-id="element-3710" data-mp="bz54t5mgl7s1" />
            <button type="button" aria-label={t('Dicter', 'Dictate')} className="p-1 text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0" data-id="element-3711" data-mp="hxiky4y5cj8">
              <MicIcon className="w-5 h-5" data-id="element-3712" data-mp="3nuq20nj6psof" />
            </button>
            <button type="button" onClick={submitReply} disabled={!input.trim()} aria-label={t('Envoyer à ATNA', 'Send to ATNA')} className="w-9 h-9 rounded-full bg-[#4F46E5] text-white flex items-center justify-center hover:bg-[#4338CA] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0" data-id="element-3713" data-mp="3nt5az7w2wrvb">
              <ArrowUpIcon className="w-4 h-4" data-id="element-3714" data-mp="34y8ekibdcexz" />
            </button>
          </div>

          {!started && <>
              <div className="flex flex-wrap gap-2 mt-3" data-id="element-3715" data-mp="135k8m17cbdei">
                {SUGGESTIONS.map(s => <button key={s.fr} type="button" onClick={() => submitBrief(lang === 'fr' ? s.fr : s.en)} className="px-3.5 py-2 rounded-full border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors" data-id="element-3716" data-mp="y5eemedllet9">
                    {lang === 'fr' ? s.fr : s.en}
                  </button>)}
              </div>
              <p className="flex items-center gap-1.5 text-xs font-medium text-[#4F46E5] mt-3" data-id="element-3717" data-mp="mqukp47kj1ke">
                <SparklesIcon className="w-3.5 h-3.5" data-id="element-3718" data-mp="rpfhetnm3343" />
                {t('ATNA connaît les services de Windsor', 'ATNA knows the City of Windsor services')}
              </p>
            </>}

          <p className="text-[11px] text-slate-400 mt-3" data-id="element-3719" data-mp="10rov1se6fclp">
            {t('ATNA peut se tromper. Vérifiez l’avis généré avant de l’envoyer.', 'ATNA can make mistakes. Review the generated notice before sending it.')}
          </p>
        </div>
      </div>
    </section>;
}